// Shared, browser-safe policy. Camera moves never create additional sources.
export type VisualScene = {
  id: string;
  beat: string;
  search: string[];
  must_show: string;
  startRatio: number;
  endRatio: number;
};

export type VisualAsset = {
  url: string;
  originalUrl: string;
  sourceUrl: string;
  sourceTitle: string;
  creator: string;
  license: string;
  licenseUrl: string;
  attribution: string;
  retrievedAt: string;
  width: number;
  height: number;
  mime: string;
  provider: string;
  assetKey: string;
  sha256?: string;
  perceptualHash?: string;
  subject?: string;
  concept?: string;
  beat?: string;
  sceneId?: string;
  startRatio?: number;
  endRatio?: number;
  checkedAt?: string;
  relevance?: "reviewed";
  rightsVerified?: boolean;
  restrictions?: string;
  seconds?: number;
  reviewStatus?: 'candidate' | 'approved' | 'rejected';
};

export function commerciallyReusable(license: string, licenseUrl: string): boolean {
  const value = license.trim().toLowerCase();
  if (/\bnc\b|non.?commercial|\bnd\b|no.?deriv/i.test(value)) return false;
  if (['cc0', 'pdm', 'public domain'].includes(value))
    return new RegExp(`^https://creativecommons\\.org/publicdomain/${value === 'cc0' ? 'zero' : 'mark'}/`).test(licenseUrl);
  const match = value.match(/^cc[- ](by(?:[- ]sa)?)(?:[- ](1\.0|2\.0|2\.5|3\.0|4\.0))?$/);
  if (match) {
    const expected = match[1].replace(' ', '-');
    const urlMatch = licenseUrl.match(/^https:\/\/creativecommons\.org\/licenses\/(by(?:-sa)?)\/(1\.0|2\.0|2\.5|3\.0|4\.0)(?:\/|$)/i);
    return Boolean(urlMatch && urlMatch[1] === expected && (!match[2] || urlMatch[2] === match[2]));
  }
  return false;
}

export function visualIdentity(url: string): string {
  try {
    const parsed = new URL(url, 'https://smartasaurus.com');
    if (parsed.hostname === 'images.weserv.nl') return visualIdentity(parsed.searchParams.get('url') ?? url);
    const path = decodeURIComponent(parsed.pathname)
      .replace('/storage/v1/render/image/', '/storage/v1/object/')
      .replace(/\/thumb\/(.+)\/\d+px-[^/]+$/, '/$1');
    return parsed.hostname + path;
  } catch { return url.split('?')[0]; }
}

export function nearDuplicate(a: Partial<Pick<VisualAsset, 'assetKey' | 'originalUrl' | 'url' | 'sha256' | 'perceptualHash'>>, b: Partial<Pick<VisualAsset, 'assetKey' | 'originalUrl' | 'url' | 'sha256' | 'perceptualHash'>>): boolean {
  if (a.assetKey && a.assetKey === b.assetKey) return true;
  if (visualIdentity(a.originalUrl ?? a.url ?? '') === visualIdentity(b.originalUrl ?? b.url ?? '')) return true;
  if (a.sha256 && a.sha256 === b.sha256) return true;
  if (a.perceptualHash?.length === 64 && b.perceptualHash?.length === 64) {
    const distance = [...a.perceptualHash].filter((bit, i) => bit !== b.perceptualHash?.[i]).length;
    if (distance <= 6) return true;
  }
  return false;
}

export function assetBlockers(item: Partial<VisualAsset>, now = Date.now()): string[] {
  const reasons: string[] = [];
  if (!item.url || !item.originalUrl || !/^https:\/\//.test(item.originalUrl)) reasons.push('missing original asset');
  if (!item.sourceUrl || !/^https:\/\//.test(item.sourceUrl)) reasons.push('missing original source page');
  if (!item.creator || !item.attribution || !item.retrievedAt) reasons.push('missing creator, attribution or retrieval date');
  if (!commerciallyReusable(item.license ?? '', item.licenseUrl ?? '') || !item.rightsVerified) reasons.push('commercial reuse rights unverified');
  if ((item.width ?? 0) < 1000 || (item.height ?? 0) < 600) reasons.push('insufficient resolution (1000 x 600 minimum)');
  if (!item.checkedAt || !Number.isFinite(Date.parse(item.checkedAt)) || now - Date.parse(item.checkedAt) > 30 * 86400000) reasons.push('availability check missing or stale');
  if (item.relevance !== 'reviewed' || item.reviewStatus !== 'approved' || !item.subject || !item.beat || !item.sceneId) reasons.push('narrative relevance awaits editorial review');
  if (!item.sha256 || !item.perceptualHash) reasons.push('duplicate check missing');
  return reasons;
}

export function validVisualAssets(value: unknown, now = Date.now()): VisualAsset[] {
  const items = Array.isArray(value) ? value : (value as { items?: unknown[] } | null)?.items ?? [];
  const valid: VisualAsset[] = [];
  for (const raw of items) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as VisualAsset;
    if (assetBlockers(item, now).length || valid.some((old) => nearDuplicate(old, item))) continue;
    valid.push(item);
  }
  return valid;
}

// A scarce legacy gallery remains a static cover, never a simulated video.
export function playbackVisuals<T extends { url: string; originalUrl?: string; assetKey?: string; sha256?: string; perceptualHash?: string; startRatio?: number; reviewStatus?: string }>(items: T[]): T[] {
  const unique: T[] = [];
  for (const item of items) if (item.url && (!item.reviewStatus || item.reviewStatus === 'approved') && !unique.some((old) => nearDuplicate(old, item))) unique.push(item);
  return unique.length >= 8 ? unique.sort((a, b) => (a.startRatio ?? 0) - (b.startRatio ?? 0)) : [];
}

// One traversal across the narration; no modulo, shuffled repeats or fake crops.
export function visualClock(items: Array<{ startRatio?: number; endRatio?: number }>, elapsed: number, duration: number) {
  if (!items.length) return { index: 0, progress: 0 };
  const ratio = Math.max(0, Math.min(1, elapsed / Math.max(1, duration)));
  let index = Math.min(items.length - 1, Math.floor(ratio * items.length));
  const timed = items.every((item) => Number.isFinite(item.startRatio) && Number.isFinite(item.endRatio));
  if (timed) {
    index = 0;
    items.forEach((item, i) => { if (ratio >= (item.startRatio ?? 0)) index = i; });
  }
  const start = timed ? items[index].startRatio ?? 0 : index / items.length;
  const end = timed ? items[index].endRatio ?? 1 : (index + 1) / items.length;
  return { index, progress: Math.max(0, Math.min(1, (ratio - start) / Math.max(0.001, end - start))) };
}